import { Tag } from '@components';
import { Divider, Popover, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import type { Icon } from 'iconsax-react';
import { type FC, useEffect, useRef, useState } from 'react';

import { hasValue } from '@core/utils/equality';
import { pluralize } from '@core/utils/pluralize';

type Props = {
  entityName: string;
  entities: string[];
  icon?: Icon;
  count?: number;
  emptyLabel?: string;
};

export const CountBadge: FC<Props> = ({
  entityName,
  entities,
  icon: EntityIcon,
  count,
  emptyLabel,
}) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = (): void => {
    if (hasValue(closeTimeoutRef.current)) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = (): void => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 100);
  };

  useEffect(() => cancelClose, []);

  const total = count ?? entities.length;
  if (total === 0 && !hasValue(emptyLabel)) {
    return '-';
  }

  const showsSingleName = total === 1 && entities.length === 1;
  const countTitle = showsSingleName ? entities[0] : `${total} ${pluralize(total, entityName)}`;
  const title = total === 0 && hasValue(emptyLabel) ? emptyLabel : countTitle;

  const icon = hasValue(EntityIcon) && (
    <EntityIcon size={20} variant='TwoTone' color={theme.palette.SlabIndigo[700]} />
  );

  const badge = (
    <Tag
      ref={anchorRef}
      variant='Secondary'
      label={title}
      renderLeft={icon}
      onMouseEnter={() => {
        cancelClose();
        setIsOpen(true);
      }}
      onMouseLeave={scheduleClose}
    />
  );

  if (showsSingleName || entities.length === 0) {
    return badge;
  }

  const sortedNames = [...entities].sort();

  return (
    <>
      {badge}
      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: {
              pointerEvents: 'auto',
              mt: '0.25rem',
              p: '1rem',
              borderRadius: '0.75rem',
              maxWidth: '20rem',
              boxShadow: '0 8px 24px rgba(9, 23, 71, 0.12)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {icon}
          <Typography
            sx={{ fontWeight: 600, fontSize: '1rem', color: theme.palette.SlabGray[800] }}
          >
            {title}
          </Typography>
        </Box>
        <Divider sx={{ my: '0.75rem' }} />
        <Box component='ul' sx={{ m: 0, pl: '1.25rem', display: 'grid', gap: '0.5rem' }}>
          {sortedNames.map((name, index) => (
            <Typography
              component='li'
              // eslint-disable-next-line react/no-array-index-key
              key={`${name}-${index}`}
              sx={{ color: theme.palette.SlabGray[800] }}
            >
              {name}
            </Typography>
          ))}
        </Box>
      </Popover>
    </>
  );
};
